interface ApiEndpoint {
  method: string;
  path: string;
  description: string;
  authRequired: boolean;
  requestBody?: any;
  responseBody?: any;
  curlExample?: string;
}

interface ApiMethod {
  method: string;
  badge: string;
  color: string;
}

const methodStyles: Record<string, ApiMethod> = {
  GET: { method: 'GET', badge: 'GET', color: 'text-blue-400 bg-blue-500/10 border-blue-500/30' },
  POST: { method: 'POST', badge: 'POST', color: 'text-green-400 bg-green-500/10 border-green-500/30' },
  PUT: { method: 'PUT', badge: 'PUT', color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30' },
  PATCH: { method: 'PATCH', badge: 'PATCH', color: 'text-orange-400 bg-orange-500/10 border-orange-500/30' },
  DELETE: { method: 'DELETE', badge: 'DELETE', color: 'text-red-400 bg-red-500/10 border-red-500/30' },
};

function EndpointCard({ endpoint }: { endpoint: ApiEndpoint }) {
  const style = methodStyles[endpoint.method] || methodStyles.GET;

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden hover:border-zinc-700 transition-colors">
      {/* Header */}
      <div className="p-4 border-b border-zinc-800 bg-zinc-900/80">
        <div className="flex items-start gap-3">
          <span className={`px-2 py-1 text-xs font-mono font-bold rounded border ${style.color}`}>
            {style.badge}
          </span>
          <div className="flex-1">
            <code className="text-sm text-white font-mono break-all">{endpoint.path}</code>
            <p className="text-sm text-zinc-400 mt-1">{endpoint.description}</p>
            {endpoint.authRequired && (
              <div className="flex items-center gap-1 mt-2">
                <span className="material-icons text-orange-400 text-xs">lock</span>
                <span className="text-xs text-orange-400">Requires Authentication</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-4">
        {/* Request Body */}
        {endpoint.requestBody && (
          <div>
            <h4 className="text-xs font-semibold text-zinc-400 uppercase mb-2">Request Body</h4>
            <pre className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 overflow-x-auto">
              <code className="text-xs text-cyan-400">{JSON.stringify(endpoint.requestBody, null, 2)}</code>
            </pre>
          </div>
        )}

        {/* Response Body */}
        {endpoint.responseBody && (
          <div>
            <h4 className="text-xs font-semibold text-zinc-400 uppercase mb-2">Response Body</h4>
            <pre className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 overflow-x-auto">
              <code className="text-xs text-emerald-400">{JSON.stringify(endpoint.responseBody, null, 2)}</code>
            </pre>
          </div>
        )}

        {/* cURL Example */}
        {endpoint.curlExample && (
          <div>
            <h4 className="text-xs font-semibold text-zinc-400 uppercase mb-2">Example Request</h4>
            <pre className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 overflow-x-auto">
              <code className="text-xs text-zinc-300 whitespace-pre-wrap break-all">{endpoint.curlExample}</code>
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

export function AuthenticationEndpoints() {
  const endpoints: ApiEndpoint[] = [
    {
      method: 'POST',
      path: '/api/auth/register',
      description: 'Create a new user account and tenant. Optionally specify preferred_slug to choose your tenant namespace.',
      authRequired: false,
      requestBody: {
        email: 'user@example.com',
        password: 'SecurePass123!',
        name: 'John Doe',
        preferred_slug: 'mycompany',
      },
      responseBody: {
        success: true,
        user: { id: 'uuid', email: 'user@example.com' },
        token: 'jwt_token_here',
        tenant: { id: 'uuid', slug: 'mycompany', unique_id: 'a7b3c8e4' },
      },
      curlExample: `curl -X POST https://api.wiserwebcloud.com/api/auth/register \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "user@example.com",
    "password": "SecurePass123!",
    "name": "John Doe",
    "preferred_slug": "mycompany"
  }'`,
    },
    {
      method: 'POST',
      path: '/api/auth/login',
      description: 'Authenticate and receive a JWT token for API access.',
      authRequired: false,
      requestBody: {
        email: 'user@example.com',
        password: 'SecurePass123!',
      },
      responseBody: {
        success: true,
        token: 'jwt_token_here',
        user: { id: 'uuid', email: 'user@example.com', name: 'John Doe' },
      },
      curlExample: `curl -X POST https://api.wiserwebcloud.com/api/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "user@example.com",
    "password": "SecurePass123!"
  }'`,
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Authentication API</h2>
        <p className="text-zinc-400">Register accounts, login, and obtain authentication tokens.</p>
      </div>
      {endpoints.map((endpoint, idx) => (
        <EndpointCard key={idx} endpoint={endpoint} />
      ))}
    </div>
  );
}

export function ApiKeysEndpoints() {
  const endpoints: ApiEndpoint[] = [
    {
      method: 'POST',
      path: '/api/api-keys',
      description: 'Create a new API key for programmatic access. Safer than using JWT tokens for deployments.',
      authRequired: true,
      requestBody: {
        name: 'my-deployment-key',
        scopes: ['read', 'write', 'deploy'],
      },
      responseBody: {
        id: 'uuid',
        name: 'my-deployment-key',
        key: 'ww_live_XXXXXXXXXXXXXXXXXXXXX',
        scopes: ['read', 'write', 'deploy'],
        created_at: '2024-01-15T10:30:00Z',
      },
      curlExample: `curl -X POST https://api.wiserwebcloud.com/api/api-keys \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "my-deployment-key",
    "scopes": ["read", "write", "deploy"]
  }'`,
    },
    {
      method: 'GET',
      path: '/api/api-keys',
      description: 'List all API keys for your account.',
      authRequired: true,
      responseBody: {
        api_keys: [
          {
            id: 'uuid',
            name: 'my-deployment-key',
            key_preview: 'ww_live_XXXX...XXXX',
            scopes: ['read', 'write', 'deploy'],
            created_at: '2024-01-15T10:30:00Z',
            last_used: '2024-01-16T14:20:00Z',
          },
        ],
      },
      curlExample: `curl https://api.wiserwebcloud.com/api/api-keys \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN"`,
    },
    {
      method: 'DELETE',
      path: '/api/api-keys/{key_id}',
      description: 'Revoke an API key. This action is irreversible.',
      authRequired: true,
      responseBody: {
        success: true,
        message: 'API key deleted successfully',
      },
      curlExample: `curl -X DELETE https://api.wiserwebcloud.com/api/api-keys/{key_id} \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN"`,
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">API Keys Management</h2>
        <p className="text-zinc-400 mb-4">Create and manage API keys for programmatic access. Use API keys instead of JWT tokens for automated deployments.</p>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <p className="text-sm text-blue-300">
            <strong>💡 Tip:</strong> Use <code className="text-cyan-400">X-API-Key: ww_live_YOUR_KEY</code> header for authentication instead of Bearer tokens.
          </p>
        </div>
      </div>
      {endpoints.map((endpoint, idx) => (
        <EndpointCard key={idx} endpoint={endpoint} />
      ))}
    </div>
  );
}

export function ContainersApiEndpoints() {
  const endpoints: ApiEndpoint[] = [
    {
      method: 'GET',
      path: '/api/orgs/{org_slug}/apps',
      description: 'List all application containers for your organization.',
      authRequired: true,
      responseBody: {
        apps: [
          {
            id: 'uuid',
            name: 'My API Service',
            slug: 'myapi',
            runtime: 'rust',
            subdomain: 'api',
            status: 'running',
            docker_image: 'registry.wiserwebcloud.com/johndoe/myapi:v1',
            url: 'https://api.mycompany.a7b3c8e4.io.wiserwebcloud.com',
            created_at: '2024-01-15T10:30:00Z',
          },
        ],
      },
      curlExample: `curl https://api.wiserwebcloud.com/api/orgs/mycompany/apps \\
  -H "X-API-Key: ww_live_YOUR_KEY"`,
    },
    {
      method: 'POST',
      path: '/api/orgs/{org_slug}/apps',
      description: 'Deploy a new application container. IMPORTANT: Push your Docker image to registry.wiserwebcloud.com FIRST.',
      authRequired: true,
      requestBody: {
        name: 'My API Service',
        slug: 'myapi',
        runtime: 'rust',
        subdomain: 'api',
        docker_image: 'registry.wiserwebcloud.com/johndoe/myapi:v1',
        memory_limit_mb: 512,
        cpu_limit: 0.5,
        env_vars: {
          APP_ENV: 'production',
          LOG_LEVEL: 'info',
        },
      },
      responseBody: {
        success: true,
        app: {
          id: 'uuid',
          name: 'My API Service',
          slug: 'myapi',
          subdomain: 'api',
          url: 'https://api.mycompany.a7b3c8e4.io.wiserwebcloud.com',
          status: 'created',
        },
      },
      curlExample: `# Step 1: Build and push your Docker image
docker build -t registry.wiserwebcloud.com/johndoe/myapi:v1 .
docker push registry.wiserwebcloud.com/johndoe/myapi:v1

# Step 2: Deploy via API
curl -X POST https://api.wiserwebcloud.com/api/orgs/mycompany/apps \\
  -H "X-API-Key: ww_live_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "My API Service",
    "slug": "myapi",
    "runtime": "rust",
    "subdomain": "api",
    "docker_image": "registry.wiserwebcloud.com/johndoe/myapi:v1",
    "memory_limit_mb": 512,
    "cpu_limit": 0.5
  }'`,
    },
    {
      method: 'GET',
      path: '/api/orgs/{org_slug}/apps/{app_id}',
      description: 'Get details for a specific application container.',
      authRequired: true,
      responseBody: {
        id: 'uuid',
        name: 'My API Service',
        slug: 'myapi',
        runtime: 'rust',
        subdomain: 'api',
        status: 'running',
        docker_image: 'registry.wiserwebcloud.com/johndoe/myapi:v1',
        container_id: 'docker_container_id',
        url: 'https://api.mycompany.a7b3c8e4.io.wiserwebcloud.com',
        memory_limit_mb: 512,
        cpu_limit: 0.5,
        created_at: '2024-01-15T10:30:00Z',
        updated_at: '2024-01-15T10:30:00Z',
      },
      curlExample: `curl https://api.wiserwebcloud.com/api/orgs/mycompany/apps/{app_id} \\
  -H "X-API-Key: ww_live_YOUR_KEY"`,
    },
    {
      method: 'POST',
      path: '/api/orgs/{org_slug}/apps/{app_id}/start',
      description: 'Start a stopped application container.',
      authRequired: true,
      responseBody: {
        success: true,
        message: 'Container started successfully',
        status: 'running',
      },
      curlExample: `curl -X POST https://api.wiserwebcloud.com/api/orgs/mycompany/apps/{app_id}/start \\
  -H "X-API-Key: ww_live_YOUR_KEY"`,
    },
    {
      method: 'POST',
      path: '/api/orgs/{org_slug}/apps/{app_id}/stop',
      description: 'Stop a running application container.',
      authRequired: true,
      responseBody: {
        success: true,
        message: 'Container stopped successfully',
        status: 'stopped',
      },
      curlExample: `curl -X POST https://api.wiserwebcloud.com/api/orgs/mycompany/apps/{app_id}/stop \\
  -H "X-API-Key: ww_live_YOUR_KEY"`,
    },
    {
      method: 'GET',
      path: '/api/orgs/{org_slug}/apps/{app_id}/logs',
      description: 'Retrieve container logs for debugging. Essential for troubleshooting startup failures.',
      authRequired: true,
      responseBody: {
        logs: [
          '[2024-01-15 10:30:15] Starting My API Service v1.0.0',
          '[2024-01-15 10:30:15] Connecting to database...',
          '[2024-01-15 10:30:16] Running migrations...',
          '[2024-01-15 10:30:17] Listening on 0.0.0.0:8080',
        ],
      },
      curlExample: `curl https://api.wiserwebcloud.com/api/orgs/mycompany/apps/{app_id}/logs \\
  -H "X-API-Key: ww_live_YOUR_KEY"`,
    },
    {
      method: 'DELETE',
      path: '/api/orgs/{org_slug}/apps/{app_id}',
      description: 'Delete an application container. This will stop and remove the container.',
      authRequired: true,
      responseBody: {
        success: true,
        message: 'App deleted successfully',
      },
      curlExample: `curl -X DELETE https://api.wiserwebcloud.com/api/orgs/mycompany/apps/{app_id} \\
  -H "X-API-Key: ww_live_YOUR_KEY"`,
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">🐳 Container Deployment API</h2>
        <p className="text-zinc-400 mb-4">Deploy and manage Docker containers. All applications run as containers on the platform.</p>
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 space-y-2">
          <p className="text-sm text-orange-300">
            <strong>⚠️ IMPORTANT:</strong> You must push your Docker image to <code className="text-cyan-400">registry.wiserwebcloud.com</code> before deploying.
          </p>
          <p className="text-sm text-orange-300">
            We <strong>ONLY</strong> pull from our self-hosted registry (not Docker Hub/GHCR) due to licensing restrictions.
          </p>
        </div>
      </div>
      {endpoints.map((endpoint, idx) => (
        <EndpointCard key={idx} endpoint={endpoint} />
      ))}
    </div>
  );
}

export function DatabasesApiEndpoints() {
  const endpoints: ApiEndpoint[] = [
    {
      method: 'GET',
      path: '/api/orgs/{org_slug}/databases',
      description: 'List all databases for your organization.',
      authRequired: true,
      responseBody: {
        databases: [
          {
            id: 'uuid',
            name: 'my-app-db',
            slug: 'my-app-db',
            db_type: 'postgres',
            status: 'active',
            host: 'wiserweb-postgres-shared',
            port: 5432,
            database_name: 'ww_myapp_db_a7b3c8',
            username: 'ww_user_a7b3c8',
            created_at: '2024-01-15T10:30:00Z',
          },
        ],
      },
      curlExample: `curl https://api.wiserwebcloud.com/api/orgs/mycompany/databases \\
  -H "X-API-Key: ww_live_YOUR_KEY"`,
    },
    {
      method: 'POST',
      path: '/api/orgs/{org_slug}/databases',
      description: 'Provision a new database. Supports PostgreSQL, MariaDB, Redis, and MongoDB. Database is created EMPTY - your app must run migrations.',
      authRequired: true,
      requestBody: {
        db_type: 'postgres',
        name: 'my-app-db',
        slug: 'my-app-db',
      },
      responseBody: {
        success: true,
        database: {
          id: 'uuid',
          name: 'my-app-db',
          db_type: 'postgres',
          status: 'active',
          host: 'wiserweb-postgres-shared',
          port: 5432,
          database_name: 'ww_myapp_db_a7b3c8',
          username: 'ww_user_a7b3c8',
          password: 'randomly_generated_secure_password',
          connection_string: 'postgresql://ww_user_a7b3c8:****@wiserweb-postgres-shared:5432/ww_myapp_db_a7b3c8',
        },
      },
      curlExample: `curl -X POST https://api.wiserwebcloud.com/api/orgs/mycompany/databases \\
  -H "X-API-Key: ww_live_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "db_type": "postgres",
    "name": "my-app-db",
    "slug": "my-app-db"
  }'`,
    },
    {
      method: 'POST',
      path: '/api/orgs/{org_slug}/apps/{app_id}/databases',
      description: 'Link a database to an application. Auto-injects DATABASE_URL and other credentials as environment variables.',
      authRequired: true,
      requestBody: {
        database_id: 'database_uuid',
        env_prefix: 'DATABASE',
        auto_inject_env: true,
      },
      responseBody: {
        success: true,
        message: 'Database linked successfully',
        env_vars_injected: [
          'DATABASE_URL',
          'DATABASE_HOST',
          'DATABASE_PORT',
          'DATABASE_USER',
          'DATABASE_PASSWORD',
          'DATABASE_NAME',
        ],
      },
      curlExample: `curl -X POST https://api.wiserwebcloud.com/api/orgs/mycompany/apps/{app_id}/databases \\
  -H "X-API-Key: ww_live_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "database_id": "database_uuid",
    "env_prefix": "DATABASE",
    "auto_inject_env": true
  }'`,
    },
    {
      method: 'DELETE',
      path: '/api/orgs/{org_slug}/databases/{db_id}',
      description: 'Delete a database. WARNING: This is irreversible and will delete all data.',
      authRequired: true,
      responseBody: {
        success: true,
        message: 'Database deleted successfully',
      },
      curlExample: `curl -X DELETE https://api.wiserwebcloud.com/api/orgs/mycompany/databases/{db_id} \\
  -H "X-API-Key: ww_live_YOUR_KEY"`,
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">📊 Database Provisioning API</h2>
        <p className="text-zinc-400 mb-4">Provision PostgreSQL, MariaDB, Redis, and MongoDB databases via API.</p>
        <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4 space-y-2">
          <p className="text-sm text-cyan-300">
            <strong>📝 Note:</strong> Provisioned databases are <strong>EMPTY</strong>. Your application must handle schema migrations on startup.
          </p>
          <p className="text-sm text-cyan-300">
            Use migration tools: <code className="text-orange-400">sqlx::migrate!()</code>, <code className="text-orange-400">Prisma</code>, <code className="text-orange-400">Alembic</code>, etc.
          </p>
        </div>
      </div>
      {endpoints.map((endpoint, idx) => (
        <EndpointCard key={idx} endpoint={endpoint} />
      ))}
    </div>
  );
}

export function StaticSitesApiEndpoints() {
  const endpoints: ApiEndpoint[] = [
    {
      method: 'GET',
      path: '/api/deployments',
      description: 'List all static site deployments for your account.',
      authRequired: true,
      responseBody: {
        deployments: [
          {
            id: 'uuid',
            name: 'My Website',
            subdomain: 'mysite',
            status: 'active',
            url: 'https://mysite.mycompany.a7b3c8e4.io.wiserwebcloud.com',
            created_at: '2024-01-15T10:30:00Z',
          },
        ],
      },
      curlExample: `curl https://api.wiserwebcloud.com/api/deployments \\
  -H "X-API-Key: ww_live_YOUR_KEY"`,
    },
    {
      method: 'POST',
      path: '/api/deployments',
      description: 'Create a new static site deployment.',
      authRequired: true,
      requestBody: {
        name: 'My Website',
        subdomain: 'mysite',
      },
      responseBody: {
        id: 'uuid',
        name: 'My Website',
        subdomain: 'mysite',
        url: 'https://mysite.mycompany.a7b3c8e4.io.wiserwebcloud.com',
        status: 'active',
      },
      curlExample: `curl -X POST https://api.wiserwebcloud.com/api/deployments \\
  -H "X-API-Key: ww_live_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "My Website",
    "subdomain": "mysite"
  }'`,
    },
    {
      method: 'POST',
      path: '/api/files/{deployment_id}/{file_path}',
      description: 'Upload a file to a static site deployment. Supports HTML, CSS, JS, images, etc.',
      authRequired: true,
      requestBody: {
        content: '<!DOCTYPE html><html><head><title>My Site</title></head><body><h1>Hello World</h1></body></html>',
      },
      responseBody: {
        success: true,
        path: 'index.html',
        size: 124,
        url: 'https://mysite.mycompany.a7b3c8e4.io.wiserwebcloud.com/index.html',
      },
      curlExample: `curl -X POST https://api.wiserwebcloud.com/api/files/{deployment_id}/index.html \\
  -H "X-API-Key: ww_live_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "content": "<!DOCTYPE html><html>...</html>"
  }'`,
    },
    {
      method: 'POST',
      path: '/api/deployments/{deployment_id}/publish',
      description: 'Publish the static site (sync files from S3 to Nginx). Call this after uploading all files.',
      authRequired: true,
      responseBody: {
        success: true,
        files_synced: 12,
        message: 'Site published successfully',
      },
      curlExample: `curl -X POST https://api.wiserwebcloud.com/api/deployments/{deployment_id}/publish \\
  -H "X-API-Key: ww_live_YOUR_KEY"`,
    },
    {
      method: 'PATCH',
      path: '/api/deployments/{deployment_id}',
      description: 'Update deployment settings, including custom domain mapping.',
      authRequired: true,
      requestBody: {
        custom_domain: 'example.com',
      },
      responseBody: {
        success: true,
        deployment: {
          id: 'uuid',
          custom_domain: 'example.com',
          url: 'https://example.com',
        },
      },
      curlExample: `curl -X PATCH https://api.wiserwebcloud.com/api/deployments/{deployment_id} \\
  -H "X-API-Key: ww_live_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "custom_domain": "example.com"
  }'`,
    },
    {
      method: 'DELETE',
      path: '/api/deployments/{deployment_id}',
      description: 'Delete a static site deployment and all its files.',
      authRequired: true,
      responseBody: {
        success: true,
        message: 'Deployment deleted successfully',
      },
      curlExample: `curl -X DELETE https://api.wiserwebcloud.com/api/deployments/{deployment_id} \\
  -H "X-API-Key: ww_live_YOUR_KEY"`,
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">🌐 Static Sites API</h2>
        <p className="text-zinc-400 mb-4">Deploy static HTML/CSS/JS websites. Files are stored in S3 and served via CDN.</p>
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
          <p className="text-sm text-purple-300">
            <strong>💡 Workflow:</strong> Create deployment → Upload files → Publish → Site goes live
          </p>
        </div>
      </div>
      {endpoints.map((endpoint, idx) => (
        <EndpointCard key={idx} endpoint={endpoint} />
      ))}
    </div>
  );
}

export function ProjectsApiEndpoints() {
  const endpoints: ApiEndpoint[] = [
    {
      method: 'GET',
      path: '/api/orgs/{org_slug}/projects',
      description: 'List all projects for your organization with resource counts',
      authRequired: true,
      responseBody: {
        projects: [
          {
            project_id: 1,
            slug: 'production',
            name: 'Production Environment',
            description: 'Production apps and databases',
            color: '#10b981',
            stats: {
              apps_count: 5,
              databases_count: 3,
              static_sites_count: 2,
            },
          },
          {
            project_id: 2,
            slug: 'staging',
            name: 'Staging Environment',
            stats: {
              apps_count: 3,
              databases_count: 2,
              static_sites_count: 1,
            },
          },
        ],
        count: 2,
      },
      curlExample: `curl https://api.wiserwebcloud.com/api/orgs/mycompany/projects \\
  -H "X-API-Key: ww_live_YOUR_KEY"`,
    },
    {
      method: 'POST',
      path: '/api/orgs/{org_slug}/projects',
      description: 'Create a new project to organize your resources (apps, databases, static sites)',
      authRequired: true,
      requestBody: {
        name: 'Production Environment',
        slug: 'production',
        description: 'Production apps and databases',
        color: '#10b981',
      },
      responseBody: {
        project_id: 3,
        slug: 'production',
        name: 'Production Environment',
        description: 'Production apps and databases',
        color: '#10b981',
        created_at: '2024-01-15T10:30:00Z',
      },
      curlExample: `curl -X POST https://api.wiserwebcloud.com/api/orgs/mycompany/projects \\
  -H "X-API-Key: ww_live_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Production Environment",
    "slug": "production",
    "description": "Production apps and databases",
    "color": "#10b981"
  }'`,
    },
    {
      method: 'GET',
      path: '/api/orgs/{org_slug}/projects/{project}',
      description: 'Get a specific project by slug or ID with resource statistics',
      authRequired: true,
      responseBody: {
        project_id: 3,
        slug: 'production',
        name: 'Production Environment',
        description: 'Production apps and databases',
        color: '#10b981',
        created_at: '2024-01-15T10:30:00Z',
        stats: {
          apps_count: 5,
          databases_count: 3,
          static_sites_count: 2,
        },
      },
      curlExample: `curl https://api.wiserwebcloud.com/api/orgs/mycompany/projects/production \\
  -H "X-API-Key: ww_live_YOUR_KEY"`,
    },
    {
      method: 'PUT',
      path: '/api/orgs/{org_slug}/projects/{project}',
      description: 'Update project details (name, description, color)',
      authRequired: true,
      requestBody: {
        name: 'Production Environment',
        description: 'Updated description',
        color: '#3b82f6',
      },
      responseBody: {
        project_id: 3,
        slug: 'production',
        name: 'Production Environment',
        description: 'Updated description',
        color: '#3b82f6',
        updated_at: '2024-01-15T11:00:00Z',
      },
      curlExample: `curl -X PUT https://api.wiserwebcloud.com/api/orgs/mycompany/projects/production \\
  -H "X-API-Key: ww_live_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Production Environment",
    "color": "#3b82f6"
  }'`,
    },
    {
      method: 'DELETE',
      path: '/api/orgs/{org_slug}/projects/{project}',
      description: 'Delete a project (only if it contains no resources)',
      authRequired: true,
      responseBody: {
        success: true,
        message: 'Project deleted successfully',
      },
      curlExample: `curl -X DELETE https://api.wiserwebcloud.com/api/orgs/mycompany/projects/staging \\
  -H "X-API-Key: ww_live_YOUR_KEY"`,
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">📁 Project Management API</h2>
        <p className="text-zinc-400 mb-4">
          Organize your resources (apps, databases, static sites) into projects. Projects provide logical grouping for environments (production, staging), microservices, or any custom organization.
        </p>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 space-y-2">
          <p className="text-sm text-blue-300">
            <strong>Hierarchy:</strong> Organization → Projects → Resources
          </p>
          <p className="text-sm text-blue-300">
            <strong>Auto-creation:</strong> If you specify a <code className="text-cyan-400">project_slug</code> that doesn't exist when creating an app or database, the project will be created automatically. If no project is specified, resources are assigned to the "default" project.
          </p>
          <p className="text-sm text-blue-300">
            <strong>Benefits:</strong> Organize by environment, track resource counts per project, group related services together
          </p>
        </div>
      </div>
      {endpoints.map((endpoint, idx) => (
        <EndpointCard key={idx} endpoint={endpoint} />
      ))}
    </div>
  );
}

export function UniversalEndpointSection() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">📡 Universal API Endpoint</h2>
        <p className="text-zinc-400 mb-4">
          Self-documenting API endpoint designed for both humans and AI agents. Query this endpoint to discover all available APIs.
        </p>
      </div>

      <div className="bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/30 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <span className="material-icons text-purple-400 text-3xl">api</span>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-white mb-2">GET /.well-known/wiserweb-api</h3>
            <p className="text-zinc-300 mb-4">
              Returns comprehensive, machine-readable API documentation. Supports both authenticated and unauthenticated requests.
            </p>
            
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-zinc-400 uppercase mb-2">Unauthenticated Request</h4>
                <pre className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 overflow-x-auto">
                  <code className="text-xs text-zinc-300">curl https://api.wiserwebcloud.com/.well-known/wiserweb-api</code>
                </pre>
                <p className="text-xs text-zinc-500 mt-2">Returns: Platform info, registration instructions, and public API documentation</p>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-zinc-400 uppercase mb-2">Authenticated Request</h4>
                <pre className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 overflow-x-auto">
                  <code className="text-xs text-zinc-300">{`curl https://api.wiserwebcloud.com/.well-known/wiserweb-api \\
  -H "X-API-Key: ww_live_YOUR_KEY"`}</code>
                </pre>
                <p className="text-xs text-zinc-500 mt-2">Returns: Personalized docs with your tenant namespace, ready-to-use examples, and deployment workflows</p>
              </div>

              <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-white mb-2">Response Includes:</h4>
                <ul className="text-sm text-zinc-400 space-y-1">
                  <li>• Platform information and base URLs</li>
                  <li>• Your tenant namespace and slug</li>
                  <li>• All available endpoints with examples</li>
                  <li>• Authentication methods and API key creation</li>
                  <li>• Complete deployment workflows</li>
                  <li>• Database migration patterns</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

